import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams, useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { BookOpen, Columns3 } from 'lucide-react';
import { useListNav } from '../../hooks/useListNav';
import useListQueryState from '../../hooks/useListQueryState';
import SearchAutocomplete from '../../components/SearchAutocomplete';
import Pagination from '../../components/Pagination';
import ListCard from '../../components/ListCard';
import ErrorBoundary from '../../components/ErrorBoundary';
import { loadFailureText } from '../../utils/loadFailure';
import FavoritesFilterToggle from '../../components/FavoritesFilterToggle';
import SavedSearches, { captureQuery, STRIPPED_PARAMS } from '../../components/generic/SavedSearches';
import ActiveFilterChips from '../../components/generic/ActiveFilterChips';
import AttrBadge from '../../components/generic/AttrBadge';
import LetterFilter from '../../components/generic/LetterFilter';
import FilterPanel from '../../components/generic/FilterPanel';
import ListCardBody from '../../components/generic/ListCardBody';
import { matchedInText } from '../../utils/matchedIn';
import RandomEntry from '../../components/generic/RandomEntry';
import AllowedSourcesNotice from '../../components/generic/AllowedSourcesNotice';
import {
  optionsOf, optionLabel, rangeOptions,
  boundParamBase, hasDerivedBounds, isNegatable,
} from '../../components/generic/FilterControl';
import apiClient from '../../config/axios';
import { MAX_COMPARE } from './GenericCompare';
import { getFamily } from '../../config/familyRegistry';
import { getActiveSystem } from '../../config/system';
import { getAllowedSources } from '../../config/allowedSources';
import { useAuth } from '../../context/AuthContext';
import { useSystem } from '../../context/SystemContext';
import useOtherSystemMatches, { otherSystemHref, articleFor } from '../../hooks/useOtherSystemMatches';
import { track } from '../../utils/analytics';

/**
 * The one list page (05 §1.2), driven by Family metadata.
 *
 * Nothing below names an entity: the title, the card, the search endpoint, the
 * payload key and every filter control come from the registry.
 *
 * THE NAV CONTRACT'S FOUR POINTS (CLAUDE.md, 05 §4.3) are the whole reason this
 * component is shaped the way it is:
 *
 *  1. **Filters live in the URL.** Every control writes through one
 *     `setSearchParams` call — one user action, one history entry — and NOTHING
 *     writes a default, so the mount-time-`updateFilter` pitfall (CLAUDE.md,
 *     commit 5ac213f) is structurally impossible. `useListQueryState` owns that.
 *  2. **`saveState` on item click**, so `useListNav` can restore page, filters
 *     and scroll on the way back. Declared filters ride in the cache under
 *     `filters`, keyed by `url_key`, so the restore round-trips by construction
 *     rather than by a per-page list of state variables.
 *  3. **`state={{ from: pathname+search }}` on every row Link**, which is what
 *     lets the detail page's "Back to X" return to the exact filtered URL.
 *  4. `backTo` is read on the detail side (`GenericDetail`).
 *
 * `ErrorBoundary` is mounted around the results body — built but never mounted
 * before now (02b Part 5 #6).
 *
 * THE PLATFORM FILTERS ARE NOT DECLARED FILTERS, and that distinction is why
 * they are in this file rather than in the registry: name search, the A–Z
 * letter row, the favorites toggle and `?rulebook=` exist on every list and no
 * Family authors them (04 §4.2). `?rulebook=` gets a control only when the API
 * answers with a book vocabulary, which it does exactly for printed content.
 */

const PAGE_SIZES = [20, 50, 100, 200];

/**
 * The platform book filter, shaped like a declared one so one code path serves
 * both.
 *
 * `optionsKey` is the one place the two shapes differ, and it is load-bearing: a
 * DECLARED filter's vocabulary is keyed by its own URL parameter (04 §4.6), but
 * the book vocabulary is not a declared filter's — it is the platform's, and
 * `/filters` has always served it under the plural `rulebooks`. Looking it up
 * under `rulebook` found nothing, and a dropdown with no options does not render
 * at all, so the `?rulebook=` control was silently missing from every converted
 * list page between R8b and R9.
 *
 * Neither parity instrument could see it: the API diff compares payloads and the
 * payload was correct, and the DOM diff reads `innerText`, which omits the text
 * of a collapsed panel — the shipped Spells page keeps its book dropdown behind
 * its own toggle and the generic panel keeps everything behind one. It was found
 * by the hand-written `?rulebook=` test, which is the one thing in
 * `urlParams.test.js` that no other instrument covers.
 */
const RULEBOOK_FILTER = {
  key: 'rulebook',
  urlKey: 'rulebook',
  apiKey: 'rulebook',
  optionsKey: 'rulebooks',
  urlAliases: [],
  style: 'dropdown',
  label: 'Rulebook',
  filterLabel: 'Rulebook',
  filterPanel: true,
  unfilteredValues: [],
};

/** Where a filter's option vocabulary lives in the `/filters` response. */
const optionsKeyOf = (filter) => filter.optionsKey || filter.urlKey;

/**
 * The per-card compare toggle (issue 188). It sits inside the card's <Link>,
 * so it follows FavoriteButton's contract exactly: preventDefault +
 * stopPropagation, never navigate, never bubble to the card's onClick.
 */
function CompareToggle({ selected, atCap, name, onToggle }) {
  const disabled = !selected && atCap;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) onToggle();
      }}
      className="flex-shrink-0 p-1 mt-1 transition-transform duration-150 hover:scale-110"
      aria-pressed={selected}
      aria-label={selected ? `Remove ${name} from comparison` : `Add ${name} to comparison`}
      title={disabled
        ? `Up to ${MAX_COMPARE} can be compared at once`
        : (selected ? 'Remove from comparison' : 'Add to comparison')}
    >
      <Columns3
        className="w-5 h-5"
        style={{ color: selected ? '#C9A84C' : 'var(--text-muted)' }}
      />
    </button>
  );
}

export default function GenericList({ segment }) {
  const family = getFamily(segment, getActiveSystem());
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { saveState, restoreScroll, restored } = useListNav(`/${segment}`);
  const { isAuthenticated } = useAuth();

  const plural = family.labels.plural;
  const lowerPlural = plural.toLowerCase();

  const [filterOptions, setFilterOptions] = useState({});
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // The declared panel filters, plus the platform book control once the API has
  // told us this Family is printed content.
  const declared = family.filters;
  const filters = useMemo(
    () => (filterOptions.rulebooks ? [...declared, RULEBOOK_FILTER] : declared),
    [declared, filterOptions.rulebooks]
  );
  const {
    values: filterValues, updateFilter, resetValues, apiParams, isActive,
  } = useListQueryState(filters, restored);

  /**
   * The derived advanced-filter parameters — a `range` filter's custom bounds
   * (`?min_price=`/`?max_price=`), any numeric filter's Min/Max span
   * (`?min_cr=`, issue 187) and a dropdown's exclusion (`?type_not=`).
   *
   * They live beside the declared filters rather than inside one because they
   * are their OWN URL parameters, which is the shipped `range` contract: the
   * bucket select writes `?price_filter=` and the two inputs write
   * `?min_price=`/`?max_price=`. One filter, up to four parameters.
   */
  const extraParamDefs = useMemo(() => {
    const defs = [];
    for (const f of filters) {
      if (f.style === 'range' || hasDerivedBounds(f)) {
        const base = boundParamBase(f);
        defs.push({ key: `min_${base}`, filter: f, kind: 'min' });
        defs.push({ key: `max_${base}`, filter: f, kind: 'max' });
      }
      if (isNegatable(f)) defs.push({ key: `${f.urlKey}_not`, filter: f, kind: 'not' });
    }
    return defs;
  }, [filters]);
  const rangeBoundKeys = useMemo(() => extraParamDefs.map((d) => d.key), [extraParamDefs]);
  const [rangeBounds, setRangeBounds] = useState(() => {
    const initial = {};
    for (const key of rangeBoundKeys) {
      const cached = restored.rangeBounds ? restored.rangeBounds[key] : undefined;
      initial[key] = cached !== undefined ? cached : (searchParams.get(key) || '');
    }
    return initial;
  });

  // A parameter whose control arrives LATE still initializes from the URL. The
  // platform book filter exists only once `/filters` has answered, so on a
  // fresh load of a shared `?rulebook_not=` link its key is not in the state
  // the mount built — and a key the state does not carry is a parameter the
  // fetch never sends. Merging the missing keys in when the key set grows is
  // what makes such a link filter on first paint rather than only after the
  // control is touched.
  useEffect(() => {
    setRangeBounds((prev) => {
      const missing = rangeBoundKeys.filter((key) => prev[key] === undefined);
      if (!missing.length) return prev;
      const next = { ...prev };
      for (const key of missing) next[key] = searchParams.get(key) || '';
      return next;
    });
    // searchParams is deliberately not a dependency: URL → state resync is the
    // effect below; this one only seeds keys that did not exist at mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeBoundKeys]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState(restored.search || searchParams.get('search') || '');
  const [search, setSearch] = useState(restored.search || searchParams.get('search') || '');
  const [letter, setLetter] = useState(restored.letter || searchParams.get('letter') || '');
  const [favoritesOnly, setFavoritesOnly] = useState(
    restored.favoritesOnly || searchParams.get('favorites') === 'true' || false
  );
  const [resultsPerPage, setResultsPerPage] = useState(
    restored.resultsPerPage || parseInt(searchParams.get('limit'), 10) || family.list.defaultLimit
  );
  const [currentPage, setCurrentPage] = useState(
    restored.page || parseInt(searchParams.get('page'), 10) || 1
  );
  // The sort select (05 §1.3). Derived from the Family's `sortable` attributes,
  // so it exists exactly for a Family that marks a second one — today Items and
  // only Items, which is the shipped fact that it is the one list with a sort
  // control (02b:69).
  const [sortBy, setSortBy] = useState(restored.sortBy || searchParams.get('sort') || '');
  // The compare selection tray (issue 188). The selection is view state rather
  // than an address — it rides the useListNav cache (so back-from-detail
  // restores it) and never the URL; only the tray's Compare link writes an
  // address, and that address (`?compare=`) is the shareable artifact.
  const [compareSel, setCompareSel] = useState(restored.compareSelection || []);
  const [pagination, setPagination] = useState({});
  const abortRef = useRef(null);

  const updateURLParams = (updates) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === '' || value === null || value === undefined) next.delete(key);
      else next.set(key, value.toString());
    });
    setSearchParams(next);
  };

  // `/filters` once per mount, and only for a Family that declares one. It also
  // supplies the platform book vocabulary, which is what decides whether the
  // `?rulebook=` control exists at all.
  const filtersLoadingRef = useRef(false);
  useEffect(() => {
    if (!declared.length || filtersLoaded || filtersLoadingRef.current) return;
    filtersLoadingRef.current = true;
    apiClient.get(`/api/${segment}/filters`)
      .then((response) => { setFilterOptions(response.data || {}); setFiltersLoaded(true); })
      .catch((err) => { console.error(`Error fetching ${segment} filters:`, err); })
      .finally(() => { filtersLoadingRef.current = false; });
  }, [segment, declared.length, filtersLoaded]);

  const filterParams = apiParams(rangeBounds);
  const filterKey = new URLSearchParams(filterParams).toString();

  // Allowed sources (issue #310). `?sources=all` is the one-click bypass the
  // notice below writes — URL state, so a bypassed view is shareable and the
  // back button restores it. It is read straight off searchParams (no mirror
  // state): a URL change refetches via the dependency below, and useListNav
  // round-trips it inside `state.from` like any other query param.
  const sourcesParam = searchParams.get('sources') || '';
  // When the URL carries an explicit book filter the server does not apply
  // the saved set at all (rule 1), so the notice has nothing to announce.
  const explicitBookFilter = !!(searchParams.get('rulebook') || searchParams.get('rulebook_not'));
  // The saved set this list's read was scoped by, or null when nothing was (signed out, bypassed, or a book named).
  const savedSourcesApplied = isAuthenticated && sourcesParam === '' && !explicitBookFilter
    ? getAllowedSources(getActiveSystem()) : null;

  // Issue 1452: a search that finds nothing here asks the other enabled
  // systems, once its own answer is in, so the empty state can say where the
  // match is. Only the name search rides along: it is the one thing every
  // system's list shares.
  const { system: activeSystem, systems: enabledSystems, setSystem } = useSystem();
  const otherMatches = useOtherSystemMatches(
    search,
    !loading && !error && rows.length === 0 && !savedSourcesApplied,
    (slug, q, signal) => apiClient.get(`/api/${segment}`, { params: { search: q, page: 1, limit: 3, system: slug }, signal })
      .then((res) => {
        const titleKey = getFamily(segment, slug)?.list?.card?.title || 'name';
        return (res.data?.[segment] || []).map((row) => ({ slug: row.slug, name: row[titleKey] || row.name || row.slug }));
      }),
  );
  const activeSystemName = ((enabledSystems || []).find((s) => s.slug === activeSystem) || {}).name;

  const fetchRows = useCallback(async () => {
    // Latest-wins concurrency: abort any in-flight request rather than skipping
    // this one, so results never lag the URL.
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: resultsPerPage,
        ...(search && { search }),
        ...(letter && { starts_with: letter }),
        ...(sortBy && { sort: sortBy }),
        ...(favoritesOnly && { favorites: 'true' }),
        ...(sourcesParam && { sources: sourcesParam }),
        ...Object.fromEntries(new URLSearchParams(filterKey)),
      });
      const response = await apiClient.get(`/api/${segment}?${params}`, { signal: controller.signal });
      setRows(response.data[segment] || []);
      setPagination(response.data.pagination || {});
      setError(null);
      setLoading(false);
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      setError(loadFailureText(err, plural));
      console.error(`Error fetching ${segment}:`, err);
      setLoading(false);
    }
  }, [segment, plural, currentPage, resultsPerPage, search, letter, sortBy, favoritesOnly, filterKey, sourcesParam]);

  useEffect(() => {
    fetchRows();
    return () => abortRef.current?.abort();
  }, [fetchRows]);

  // One-directional URL → state resync for the PLATFORM controls, so browser
  // back/forward between two states of this list updates them and, via the
  // fetch effect, the results. The declared filters resync inside
  // `useListQueryState`. It SKIPS the first render: mount state already came
  // from the useListNav restore or the URL, and resyncing here would clobber it.
  const syncedRef = useRef(searchParams.toString());
  useEffect(() => {
    if (syncedRef.current === searchParams.toString()) return;
    syncedRef.current = searchParams.toString();
    setSearchInput(searchParams.get('search') || '');
    setSearch(searchParams.get('search') || '');
    setLetter(searchParams.get('letter') || '');
    setFavoritesOnly(searchParams.get('favorites') === 'true');
    setCurrentPage(parseInt(searchParams.get('page'), 10) || 1);
    setResultsPerPage(parseInt(searchParams.get('limit'), 10) || family.list.defaultLimit);
    setSortBy(searchParams.get('sort') || '');
    setRangeBounds(Object.fromEntries(rangeBoundKeys.map((k) => [k, searchParams.get(k) || ''])));
  }, [searchParams, family.list.defaultLimit, rangeBoundKeys]);

  useEffect(() => {
    if (!loading) restoreScroll();
  }, [loading, restoreScroll]);

  const handleSearch = (e) => {
    e.preventDefault();
    // How many words, never the words: typed text can be anything, a person's name included (issue 1600).
    track('search', { page: segment, words: searchInput.trim().split(/\s+/).filter(Boolean).length });
    setSearch(searchInput.trim());
    setCurrentPage(1);
    updateURLParams({ search: searchInput.trim(), page: 1 });
  };

  const clearFilters = () => {
    track('clear-filters', { page: segment });
    setSearchInput('');
    setSearch('');
    setLetter('');
    setFavoritesOnly(false);
    setSortBy('');
    setCurrentPage(1);
    resetValues();
    setRangeBounds(Object.fromEntries(rangeBoundKeys.map((k) => [k, ''])));
    setSearchParams(new URLSearchParams());
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearch('');
    setCurrentPage(1);
    updateURLParams({ search: '', page: 1 });
  };

  const onFilterChange = (filter, value) => {
    track('filter-change', {
      page: segment,
      filter: filter.urlKey,
      value: Array.isArray(value) ? value.join(',') : String(value),
    });
    setCurrentPage(1);
    updateFilter(filter, value);
  };

  /**
   * A chip's label: the option's display NAME where the vocabulary knows it,
   * the raw value otherwise. Re-labelling once `/filters` arrives is what makes
   * a deep link like `/spells?domain=fire` show "Fire" rather than "fire" (the
   * behavior #144 needed and `Spells.js:227-233` implemented by hand).
   */
  const chipLabel = (filter, value) => {
    if (Array.isArray(value)) return `${filter.filterLabel}: ${value.join(', ')}`;
    // Matched by VALUE or by display NAME, because a reference filter accepts
    // both spellings (see `referenceClause` in routes/generic.js): the shipped
    // `/races?size=Medium` carries the name, and its chip must still read
    // `Medium` rather than falling through to `Size: Medium`.
    const vocabulary = filter.style === 'range'
      ? rangeOptions(filter)
      : optionsOf(filterOptions[optionsKeyOf(filter)]);
    const option = vocabulary.find(
      (o) => o.value === String(value) || o.label.toLowerCase() === String(value).toLowerCase()
    );
    if (option) return optionLabel(filter, option);
    if (filter.style === 'yes-no' || filter.style === 'presence') {
      return `${filter.filterLabel}: ${value === 'yes' ? 'Yes' : 'No'}`;
    }
    return `${filter.filterLabel}: ${value}`;
  };

  const chips = [
    search && { key: 'search', label: `"${search}"`, tone: 'badge-blue', onClear: clearSearch },
    letter && {
      key: 'letter',
      label: `Starts with "${letter}"`,
      tone: 'badge-green',
      onClear: () => { setLetter(''); setCurrentPage(1); updateURLParams({ letter: '', page: 1 }); },
    },
    favoritesOnly && {
      key: 'favorites',
      label: 'Favorites only',
      tone: 'badge-red',
      onClear: () => {
        setFavoritesOnly(false); setCurrentPage(1);
        updateURLParams({ favorites: '', page: 1 });
      },
    },
    ...filters
      // A filter sitting at its authored DEFAULT is not an active filter — the
      // shipped Items page rendered no chip while `property_filter` was `hide`,
      // for the same reason it wrote nothing to the URL.
      .filter((filter) => isActive(filterValues[filter.urlKey])
        && filterValues[filter.urlKey] !== filter.filterDefault)
      .map((filter) => ({
        key: filter.urlKey,
        label: chipLabel(filter, filterValues[filter.urlKey]),
        tone: 'badge-blue',
        onClear: () => onFilterChange(
          filter,
          Array.isArray(filterValues[filter.urlKey]) ? [] : (filter.filterDefault ?? '')
        ),
      })),
    // The derived advanced-filter parameters (issue 187): a bound reads as the
    // span it imposes, an exclusion as the value it removes.
    ...extraParamDefs
      .filter((d) => rangeBounds[d.key])
      .map((d) => ({
        key: d.key,
        label: d.kind === 'not'
          ? `Not ${chipLabel(d.filter, rangeBounds[d.key])}`
          : `${d.kind === 'min' ? 'Min' : 'Max'} ${d.filter.filterLabel}: ${rangeBounds[d.key]}`,
        tone: d.kind === 'not' ? 'badge-red' : 'badge-blue',
        onClear: () => {
          setRangeBounds((prev) => ({ ...prev, [d.key]: '' }));
          setCurrentPage(1);
          updateURLParams({ [d.key]: '', page: 1 });
        },
      })),
  ].filter(Boolean);

  const currentFilterState = () => ({
    page: currentPage,
    searchInput,
    search,
    letter,
    favoritesOnly,
    sortBy,
    resultsPerPage,
    filters: filterValues,
    rangeBounds,
    compareSelection: compareSel,
  });

  const card = family.list.card;
  const toggleCompare = (row) => {
    setCompareSel((prev) => {
      if (prev.some((s) => s.slug === row.slug)) return prev.filter((s) => s.slug !== row.slug);
      if (prev.length >= MAX_COMPARE) return prev;
      return [...prev, { slug: row.slug, name: row[card.title] }];
    });
  };
  const compareHref = `/${segment}?compare=${compareSel.map((s) => s.slug).join(',')}`;
  // The right-hand book cluster is opt-in per ListCard: passing a string (even
  // an empty one) selects the stacked layout. Only a printed Family gets it,
  // decided by the payload rather than by a per-page flag.
  const printed = rows.some((row) => row.rulebook_name !== undefined);

  // The narrowing params the list's own fetch sends — everything except
  // page/limit/sort, which order and slice rather than narrow. This is what the
  // random draw (issue 164) reuses, so "only use my current filters" is by
  // construction the set this list is showing.
  const randomActiveParams = {
    ...(search && { search }),
    ...(letter && { starts_with: letter }),
    ...(favoritesOnly && { favorites: 'true' }),
    // The allowed-sources bypass rides along (#310): a bypassed list must
    // draw from the same bypassed set it is showing.
    ...(sourcesParam && { sources: sourcesParam }),
    ...Object.fromEntries(new URLSearchParams(filterKey)),
  };

  return (
    <>
      <Helmet>
        <title>{plural} - The Dragon&apos;s Table</title>
        <meta name="description" content={`Browse 3.5e & Pathfinder 1e ${lowerPlural}.`} />
      </Helmet>

      <div className="space-y-6">
        <div className="page-header">
          <BookOpen className="page-icon" />
          <h1 className="page-title">{plural}</h1>
        </div>

        <div className="card">
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SearchAutocomplete
                value={searchInput}
                onChange={setSearchInput}
                placeholder={`Search ${lowerPlural}...`}
                apiEndpoint={`/api/${segment}/suggest`}
                basePath={`/${segment}`}
              />
              <div className="flex gap-2">
                <button type="submit" className="btn btn-primary flex-1">Search</button>
                <RandomEntry
                  segment={segment}
                  family={family}
                  activeParams={randomActiveParams}
                  totalMatching={pagination.total}
                  listUrl={`${location.pathname}${location.search}`}
                  onItemClick={(row) => {
                    track('item-click', { page: segment, name: row[card.title], slug: row.slug });
                    saveState(currentFilterState());
                  }}
                />
              </div>
            </div>

            <LetterFilter
              value={letter}
              onChange={(next) => {
                setLetter(next);
                setCurrentPage(1);
                updateURLParams({ letter: next, page: 1 });
              }}
            />

            {isAuthenticated && (
              <div className="flex flex-wrap items-center gap-4">
                {family.favoritable && (
                  <FavoritesFilterToggle
                    checked={favoritesOnly}
                    onChange={(checked) => {
                      track('filter-change', { page: segment, filter: 'favorites', value: String(checked) });
                      setFavoritesOnly(checked);
                      setCurrentPage(1);
                      updateURLParams({ favorites: checked ? 'true' : '', page: 1 });
                    }}
                  />
                )}
                {/* Saved searches (issue 186): save = capture the current URL
                    query (minus page and the stripped params), apply = write a
                    stored one back — the same act as loading a shared link, so
                    the URL → state resyncs above do all the work. */}
                <SavedSearches
                  segment={segment}
                  currentQuery={captureQuery(searchParams)}
                  onApply={(queryString) => {
                    track('saved-search-apply', { page: segment });
                    const next = new URLSearchParams(queryString);
                    for (const key of STRIPPED_PARAMS) next.delete(key);
                    setSearchParams(next);
                  }}
                />
              </div>
            )}

            <FilterPanel
              filters={filters}
              options={Object.fromEntries(
                filters.map((f) => [f.urlKey, optionsOf(filterOptions[optionsKeyOf(f)])])
              )}
              values={filterValues}
              onChange={onFilterChange}
              open={showFilters}
              onToggle={() => setShowFilters((v) => !v)}
              extra={rangeBounds}
              onExtraChange={(key, value) => {
                setRangeBounds((prev) => ({ ...prev, [key]: value }));
                setCurrentPage(1);
                updateURLParams({ [key]: value, page: 1 });
              }}
            />

            <ActiveFilterChips chips={chips} onClearAll={clearFilters} />
          </form>
        </div>

        {/* Allowed sources (issue #310): visible whenever the saved set is
            deciding (or being told not to decide) what this list shows; the
            empty state renders it too, because "your set narrowed this to
            nothing" is exactly when the reader needs the one-click way out. */}
        <AllowedSourcesNotice
          bypassed={sourcesParam !== ''}
          suppressed={explicitBookFilter}
          onChange={(next) => {
            track('allowed-sources-toggle', { page: segment, value: next || 'apply' });
            setCurrentPage(1);
            updateURLParams({ sources: next, page: 1 });
          }}
        />

        <ErrorBoundary>
          {error ? (
            <div className="card text-center" role="alert" aria-live="assertive">
              <p className="error-message">{error}</p>
              <button type="button" className="btn btn-primary mt-4" onClick={() => fetchRows()}>Try again</button>
            </div>
          ) : loading ? (
            <div className="flex justify-center items-center h-64" role="status" aria-live="polite">
              <div className="loading-spinner" role="status" aria-label="Loading"></div>
            </div>
          ) : rows.length === 0 ? (
            <div className="card text-center" role="status" aria-live="polite">
              {savedSourcesApplied ? (
                // The saved set hid every match (issue 817): say so, with the notice's own way out, rather than
                // "try adjusting your search" — the search may be exactly right.
                <p className="no-results" data-testid="no-results-sources">
                  No {lowerPlural} in your allowed sources ({savedSourcesApplied.length} {savedSourcesApplied.length === 1 ? 'book' : 'books'}).{' '}
                  <button type="button" className="underline" onClick={() => { setCurrentPage(1); updateURLParams({ sources: 'all', page: 1 }); }}>
                    Show all sources
                  </button>
                </p>
              ) : otherMatches.length === 0 ? (
                <p className="no-results">No {lowerPlural} found. Try adjusting your search.</p>
              ) : (
                <>
                  <p className="no-results">
                    No {lowerPlural} found{activeSystemName ? ` in ${activeSystemName}` : ''}.
                  </p>
                  {otherMatches.map(({ system: other, items }) => {
                    const singular = (getFamily(segment, other.slug)?.labels?.singular || family.labels.singular).toLowerCase();
                    return (
                      <div key={other.slug} className="mt-3" data-testid="other-system-matches">
                        {items.map((item) => (
                          <p key={item.slug} className="mt-1">
                            {item.name} is {articleFor(other.name)} {other.name} {singular}.{' '}
                            <a className="underline" href={otherSystemHref(segment, item.slug, other.slug)}>
                              Open it
                            </a>
                          </p>
                        ))}
                        <button type="button" className="btn btn-secondary mt-2" onClick={() => setSystem(other.slug)}>
                          Switch to {other.name}
                        </button>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="results-count">
                  Showing {rows.length} of {pagination.total} {lowerPlural}
                </div>
                <div className="flex items-center gap-4">
                  {family.list.sortOptions.length > 0 && (
                    <select
                      value={sortBy}
                      onChange={(e) => {
                        track('filter-change', { page: segment, filter: 'sort', value: e.target.value });
                        setSortBy(e.target.value);
                        setCurrentPage(1);
                        updateURLParams({ sort: e.target.value, page: 1 });
                      }}
                      className="results-per-page-select"
                      aria-label="Sort"
                    >
                      {family.list.sortOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  )}
                  <select
                    value={resultsPerPage}
                    onChange={(e) => {
                      const limit = parseInt(e.target.value, 10);
                      track('results-per-page', { page: segment, value: limit });
                      setResultsPerPage(limit);
                      setCurrentPage(1);
                      updateURLParams({ limit, page: 1 });
                    }}
                    className="results-per-page-select"
                    aria-label="Results per page"
                  >
                    {PAGE_SIZES.map((size) => (
                      <option key={size} value={size}>{size} per page</option>
                    ))}
                  </select>
                </div>
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={pagination.pages}
                onPageChange={(page) => { setCurrentPage(page); updateURLParams({ page }); }}
              />

              <div className="space-y-4" aria-label={`${family.labels.singular} list`}>
                {rows.map((row) => (
                  <ListCard
                    key={row.slug}
                    to={`/${segment}?slug=${row.slug}`}
                    state={{ from: `${location.pathname}${location.search}` }}
                    onClick={() => {
                      track('item-click', { page: segment, name: row[card.title], slug: row.slug });
                      saveState(currentFilterState());
                    }}
                    title={row[card.title]}
                    badges={card.badges.length ? (
                      <>
                        {card.badges.map((attr) => (
                          <AttrBadge key={attr.key} attr={attr} value={row[attr.key]} />
                        ))}
                      </>
                    ) : null}
                    rulebookName={printed ? (row.rulebook_name || '') : undefined}
                    showFavorite={isAuthenticated && family.favoritable}
                    actions={(
                      <CompareToggle
                        selected={compareSel.some((s) => s.slug === row.slug)}
                        atCap={compareSel.length >= MAX_COMPARE}
                        name={row[card.title]}
                        onToggle={() => toggleCompare(row)}
                      />
                    )}
                    entityType={segment}
                    slug={row.slug}
                  >
                    {Array.isArray(row.matched_in) && row.matched_in.length > 0 && (
                      // Kept by another book's printing (issue 1461; Tom's A): the card says where, so it agrees with the filter.
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }} data-testid="matched-in">
                        {matchedInText(row.matched_in)}
                      </p>
                    )}
                    <ListCardBody blocks={card.blocks} row={row} />
                  </ListCard>
                ))}
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={pagination.pages}
                onPageChange={(page) => { setCurrentPage(page); updateURLParams({ page }); }}
              />
            </div>
          )}
        </ErrorBoundary>

        {/* The compare tray (issue 188): appears once anything is selected,
            floats over the list, and its Compare link is the one writer of the
            `?compare=` address. It carries `state.from` like a row link, so
            the compare view's back link returns to this exact filtered URL. */}
        {compareSel.length > 0 && (
          <div
            className="fixed bottom-4 inset-x-0 z-40 flex justify-center px-4 print:hidden"
            data-testid="compare-tray"
          >
            <div className="card flex flex-wrap items-center gap-2 py-3 max-w-full shadow-lg">
              <span className="text-secondary text-sm whitespace-nowrap">
                Compare ({compareSel.length}/{MAX_COMPARE})
              </span>
              {compareSel.map(({ slug, name }) => (
                <button
                  key={slug}
                  type="button"
                  onClick={() => toggleCompare({ slug })}
                  className="badge badge-blue"
                  title={`Remove ${name} from comparison`}
                >
                  {name} ✕
                </button>
              ))}
              <Link
                to={compareHref}
                state={{ from: `${location.pathname}${location.search}` }}
                className={`btn btn-primary ${compareSel.length < 2 ? 'opacity-50 cursor-not-allowed' : ''}`}
                aria-disabled={compareSel.length < 2}
                onClick={(e) => {
                  if (compareSel.length < 2) { e.preventDefault(); return; }
                  track('compare', { page: segment, count: compareSel.length });
                }}
                data-testid="compare-go"
              >
                Compare
              </Link>
              <button
                type="button"
                onClick={() => setCompareSel([])}
                className="btn btn-secondary"
                aria-label="Clear comparison selection"
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
